import { useCallback, useEffect, useState } from "react";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch } from "../../lib/api.js";
import { useMLNodeRun } from "../MLNodeRunContext.js";

// ADR-0106 — Model Predict inspector. Binds a Model + version (by id, not an
// edge — the persistent_provider convention) and appends prediction columns
// in-hub at run time. Pure config; autosaves via setNodeData. Shows a
// compatibility note (which required features are missing upstream).

type ModelListItem = { id: string; name: string; algorithm_family: string; champion_version: number | null };
type VersionRow = {
  id: string;
  version_number: number;
  is_current: boolean;
  feature_schema: Array<{ name: string; type: string }>;
  output_contract: { columns?: Array<{ name: string; type: string }> };
};
type ModelDetail = { model: { id: string; name: string }; versions: VersionRow[] };
type PreviewPayload = { columns: Array<{ name: string; type?: string }>; rows: unknown[][]; rowCount: number };

type VersionSel = "current" | number;

function cellText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(4);
  return String(v);
}

function readSpec(data: OperationCanvasNodeData): { modelId: string | null; version: VersionSel } {
  const sp = (data.config?.spec_model_predict as Record<string, unknown> | undefined) ?? {};
  const v = sp.version;
  return {
    modelId: typeof sp.model_id === "string" && sp.model_id ? sp.model_id : null,
    version: typeof v === "number" && Number.isInteger(v) && v > 0 ? v : "current",
  };
}

export function OperationInspectorModelPredict({
  selectedNode,
  operationId,
  setNodeData,
  flushAndSave,
  pipelineModels = [],
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  flushAndSave: () => Promise<boolean>;
  /** ADR-0116 — models this pipeline's Model Train node(s) have produced, for one-click bind. */
  pipelineModels?: Array<{ id: string; name: string }>;
}) {
  const [models, setModels] = useState<ModelListItem[]>([]);
  const [modelId, setModelId] = useState<string | null>(null);
  const [version, setVersion] = useState<VersionSel>("current");
  const [isProvisional, setIsProvisional] = useState(false);
  const [detail, setDetail] = useState<ModelDetail | null>(null);
  const [upstreamColumns, setUpstreamColumns] = useState<string[]>([]);
  const [columnsError, setColumnsError] = useState<string | null>(null);

  // Feature 0010: run state lives in the editor-scoped provider so it survives
  // this inspector unmounting (tab switch / node deselect).
  const { getRun, startRun, stopRun } = useMLNodeRun();
  const run = getRun(selectedNode.id);
  const running = run.running;
  const runError = run.error;
  const preview = run.preview as PreviewPayload | null;

  const specJson = JSON.stringify(selectedNode.data.config?.spec_model_predict ?? null);

  useEffect(() => {
    const s = readSpec(selectedNode.data);
    setModelId(s.modelId);
    setVersion(s.version);
    const sp = (selectedNode.data.config?.spec_model_predict as Record<string, unknown> | undefined) ?? {};
    setIsProvisional(sp.provisional === true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id, specJson]);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ models: ModelListItem[] }>("/api/tenant/models");
        setModels(data.models ?? []);
      } catch { setModels([]); }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ columns?: Array<{ name?: string }> }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/upstream-columns`,
        );
        setUpstreamColumns((data.columns ?? []).map((c) => c.name).filter((n): n is string => typeof n === "string" && !!n.trim()));
        setColumnsError(null);
      } catch (e) { setColumnsError(e instanceof Error ? e.message : String(e)); }
    })();
  }, [operationId, selectedNode.id]);

  useEffect(() => {
    if (!modelId) { setDetail(null); return; }
    void (async () => {
      try { setDetail(await apiFetch<ModelDetail>(`/api/tenant/models/${encodeURIComponent(modelId)}`)); }
      catch { setDetail(null); }
    })();
  }, [modelId]);

  const writeSpec = useCallback((next: { model_id: string | null; version: VersionSel }) => {
    setNodeData(selectedNode.id, (d) => {
      const cfg = (d.config ?? {}) as Record<string, unknown>;
      return { ...d, config: { ...cfg, spec_model_predict: next } };
    });
  }, [selectedNode.id, setNodeData]);

  function onModel(id: string) {
    const next = id || null;
    setModelId(next); setVersion("current");
    writeSpec({ model_id: next, version: "current" });
  }
  function onVersion(v: string) {
    const next: VersionSel = v === "current" ? "current" : Number(v);
    setVersion(next); writeSpec({ model_id: modelId, version: next });
  }

  function onRun() {
    if (running || !modelId) return;
    startRun(selectedNode.id, { operationId, flushAndSave });
  }

  const selectedVersion: VersionRow | null = (() => {
    if (!detail) return null;
    if (version === "current") return detail.versions.find((v) => v.is_current) ?? null;
    return detail.versions.find((v) => v.version_number === version) ?? null;
  })();

  const missingFeatures = selectedVersion
    ? selectedVersion.feature_schema.map((f) => f.name).filter((n) => !upstreamColumns.includes(n))
    : [];
  const outputCols = selectedVersion?.output_contract?.columns ?? [];

  return (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div style={{ fontWeight: 600 }}>Model Predict</div>

      {/* ADR-0116 — architect-created node waiting to be bound to the pipeline's model. */}
      {isProvisional ? (
        <div className="card" style={{ borderColor: "var(--warn-border, var(--op-node-watch-border))", background: "var(--warn-bg, #fffbeb)", display: "grid", gap: 6 }}>
          {pipelineModels.length > 0 ? (
            <>
              <div><span className="pill pillWarn">Review</span> — bind the model this pipeline trains</div>
              <div className="actions" style={{ flexWrap: "wrap" }}>
                {pipelineModels.map((m) => (
                  <button key={m.id} className="btn btnPrimary btnSm" type="button" onClick={() => onModel(m.id)}>
                    Bind “{m.name}”
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div><span className="pill pillWarn">Needs setup</span></div>
              <div className="help">Configure and train the model in this pipeline first, then come back to bind it here.</div>
            </>
          )}
        </div>
      ) : null}

      <label className="field">
        <span className="label">Model</span>
        <select className="select" value={modelId ?? ""} onChange={(e) => onModel(e.target.value)}>
          <option value="">— select a model —</option>
          {models.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
        </select>
      </label>

      <label className="field">
        <span className="label">Version</span>
        <select className="select" value={version === "current" ? "current" : String(version)} onChange={(e) => onVersion(e.target.value)} disabled={!detail}>
          <option value="current">Champion (always the promoted version)</option>
          {(detail?.versions ?? []).map((v) => (
            <option key={v.id} value={String(v.version_number)}>v{v.version_number}{v.is_current ? " — champion" : ""}</option>
          ))}
        </select>
      </label>

      {!modelId ? (
        <div className="help">Pick a trained model to append its predictions to the upstream rows.</div>
      ) : !selectedVersion ? (
        <div className="help">This model has no trained version yet.</div>
      ) : (
        <>
          {columnsError ? (
            <div className="help">Run the upstream node once to check schema compatibility.</div>
          ) : missingFeatures.length > 0 ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>
              Upstream is missing required feature{missingFeatures.length > 1 ? "s" : ""}: {missingFeatures.join(", ")}
            </div>
          ) : (
            <div className="help">All required features are present upstream.</div>
          )}
          {outputCols.length > 0 ? (
            <div className="help">Appends columns: {outputCols.map((c) => c.name).join(", ")}</div>
          ) : null}
        </>
      )}

      <div className="actions">
        {running ? (
          <button className="btn btnDanger" type="button" onClick={() => stopRun(selectedNode.id)}>
            Stop
          </button>
        ) : (
          <button className="btn btnPrimary" type="button" onClick={() => onRun()} disabled={!modelId}>
            Run &amp; preview
          </button>
        )}
      </div>
      {running ? (
        <div className="help">Running{run.startedAt ? ` since ${new Date(run.startedAt).toLocaleString()}` : ""}…</div>
      ) : null}
      {runError ? <div className="help" style={{ color: "var(--danger-text)" }}>{runError}</div> : null}
      {preview ? (
        <div style={{ display: "grid", gap: 4 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            {preview.rowCount.toLocaleString()} rows · showing first {Math.min(preview.rows.length, 20)}
          </div>
          <div className="tableWrap" style={{ maxHeight: 320, overflow: "auto" }}>
            <table className="table tableCompact">
              <thead><tr>{preview.columns.map((c) => (<th key={c.name}>{c.name}</th>))}</tr></thead>
              <tbody>
                {preview.rows.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    {preview.columns.map((_, ci) => (<td key={ci}>{cellText((r as unknown[])[ci])}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
